import { Box, Typography } from '@mui/material';
import * as React from 'react';
import { toFixed } from '../../helpers/utilityFunctions/utilityFunctions';
import { Temperature } from '../../model/temperature';
import { getGradientStops } from './gradientHelper';
import { getTempDifferences } from './heatmapFunctions';

const SENSOR_WIDTHS = ['47.5%', '45%', '50%'] as const;
const SENSOR_TOP_POSITIONS = ['5%', '22%', '70%'] as const;

const SensorPointView = () => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: '50%',
      border: '1.5px solid rgba(255,255,255,0.5)',
      backgroundColor: 'rgba(110, 140, 170, 0.85)',
      flexShrink: 0,
      zIndex: 2,
    }}
  />
);

const GradientConnectionLine = ({
  signedDiff,
  gradientId,
}: {
  signedDiff: number;
  gradientId: string;
}) => {
  const stops = getGradientStops(Math.abs(signedDiff));
  // signedDiff >= 0 → left foot warmer → red on the left side
  const x1 = signedDiff >= 0 ? '1' : '0';
  const x2 = signedDiff >= 0 ? '0' : '1';

  return (
    <Box sx={{ flex: 1, height: 4, mx: '-1px', zIndex: 1 }}>
      <svg width="100%" height="4" style={{ display: 'block' }}>
        <defs>
          <linearGradient id={gradientId} x1={x1} y1="0" x2={x2} y2="0">
            {stops.map((s, i) => (
              <stop key={i} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>
        </defs>
        <rect x="0" y="0" width="100%" height="4" fill={`url(#${gradientId})`} />
      </svg>
    </Box>
  );
};

const SensorLineWithConnection = ({
  signedDiff,
  gradientId,
  index,
}: {
  signedDiff: number;
  gradientId: string;
  index: number;
}) => {
  const width = SENSOR_WIDTHS[index] ?? SENSOR_WIDTHS[SENSOR_WIDTHS.length - 1];

  return (
    <Box sx={{ width, display: 'flex', flexDirection: 'row', justifyContent: 'center', alignItems: 'center' }}>
      <SensorPointView />
      <GradientConnectionLine signedDiff={signedDiff} gradientId={gradientId} />
      <SensorPointView />
    </Box>
  );
};

const SensorValueIndicator = ({
  signedDiff,
  unit,
  index,
}: {
  signedDiff: number;
  unit: Temperature;
  index: number;
}) => {
  const top = SENSOR_TOP_POSITIONS[index] ?? SENSOR_TOP_POSITIONS[SENSOR_TOP_POSITIONS.length - 1];
  const absDiff = Math.abs(signedDiff);

  return (
    <Box
      sx={{
        position: 'absolute',
        top,
        left: 0,
        right: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography
        sx={{
          color: '#101820',
          fontSize: '0.8rem',
          fontWeight: 600,
          textShadow: '0 0 4px rgba(255,255,255,0.9), 0 1px 3px rgba(255,255,255,0.7)',
          mb: '2px',
          transform: index === 2 ? 'translateY(6px)' : 'translateY(4px)',
        }}
      >
        {parseFloat(toFixed(absDiff, 1))}
        {unit === Temperature.Celsius ? '°C' : '°F'}
      </Typography>
      <SensorLineWithConnection
        signedDiff={signedDiff}
        gradientId={`lineGrad-${index}`}
        index={index}
      />
    </Box>
  );
};

interface DifferenceOverlayProps {
  sensorDataLeft: string;
  sensorDataRight: string;
  unit: Temperature;
}

function DifferenceOverlay({
  sensorDataLeft,
  sensorDataRight,
  unit,
}: DifferenceOverlayProps): React.JSX.Element | null {
  const signedDiffs = getTempDifferences(sensorDataLeft, sensorDataRight);

  if (!signedDiffs || signedDiffs.length < 1) return null;

  return (
    <Box
      sx={{
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        position: 'absolute',
      }}
    >
      {signedDiffs.slice(0, 3).map((signedDiff, index) => (
        <SensorValueIndicator
          key={index}
          signedDiff={signedDiff}
          unit={unit}
          index={index}
        />
      ))}
    </Box>
  );
}

export default DifferenceOverlay;
